import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Menu, MenuItem } from "@mui/material";
import UpIcon from "@mui/icons-material/ExpandLessRounded";
import DownIcon from "@mui/icons-material/ExpandMoreRounded";

import "./InputOptionCheckbox.css";
import { useLocation, useNavigate } from "react-router-dom";
import {
  generateUrlWithParams,
  paramParsify,
  paramStringify,
} from "utils/queryParams";
import { uniq } from "lodash";

const InputOptionCheckbox = ({
  disabled,
  dynamicLabel,
  label,
  options,
  value,
  paramsKey = "params_key",
  paramsObjToOverride = {},
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const parsed = useMemo(() => paramParsify(location?.search), [location]);

  const [anchorMenu, setAnchorMenu] = useState(null);
  const [selectedOption, setSelectedOption] = useState(value);
  const isOptionOpen = Boolean(anchorMenu);

  useEffect(() => {
    setSelectedOption(value);
  }, [value]);

  const openOption = (event) => {
    setAnchorMenu(event.currentTarget);
  };

  const closeOption = () => {
    setAnchorMenu(null);
  };

  const handleOnChangeCheckbox = useCallback(
    (e) => {
      const { checked, value } = e.target;
      const checkedParamsKey = parsed?.[paramsKey] ?? [];
      const keyValues = checked
        ? [...checkedParamsKey, value]
        : [...checkedParamsKey]?.filter((el) => String(el) !== String(value));
      const cleanKeyValues = uniq(keyValues);
      const stringified = paramStringify({
        ...parsed,
        [paramsKey]: cleanKeyValues,
        ...paramsObjToOverride,
      });
      navigate(generateUrlWithParams(location?.pathname, stringified));
    },
    [location?.pathname, navigate, parsed, paramsKey, paramsObjToOverride],
  );

  const handleOnClickMenuItem = useCallback(
    (value) => {
      const checkedParamsKey = parsed?.status ?? [];
      const checked = checkedParamsKey.includes(value);
      const keyValues = !checked
        ? [...checkedParamsKey, value]
        : [...checkedParamsKey]?.filter((el) => String(el) !== String(value));
      const cleanKeyValues = uniq(keyValues);
      const stringified = paramStringify({
        ...parsed,
        [paramsKey]: cleanKeyValues,
        ...paramsObjToOverride,
      });
      navigate(generateUrlWithParams(location?.pathname, stringified));
    },
    [location?.pathname, navigate, parsed, paramsKey, paramsObjToOverride],
  );

  return (
    <div className='h-9'>
      <Button
        color={isOptionOpen ? "primary" : "secondary"}
        disabled={disabled}
        endIcon={isOptionOpen ? <UpIcon /> : <DownIcon />}
        size='small'
        variant='outlined'
        onClick={openOption}
        className='!border-[#B5BFC4]'
      >
        {dynamicLabel && selectedOption ? selectedOption.label : label}
      </Button>

      <Menu anchorEl={anchorMenu} open={isOptionOpen} onClose={closeOption}>
        {options.map((option) => (
          <MenuItem
            key={option?.id}
            onClick={() => handleOnClickMenuItem(String(option?.value))}
          >
            <div className='flex flex-row gap-2'>
              <input
                className='cursor-pointer'
                onChange={handleOnChangeCheckbox}
                type='checkbox'
                id={option?.id ?? String(option?.label)}
                name={option?.id ?? String(option?.label)}
                value={option?.value}
                checked={Boolean(
                  (typeof parsed?.[paramsKey] === "string"
                    ? [parsed?.[paramsKey]]
                    : parsed?.[paramsKey] ?? []
                  )?.find((el) => String(el) === String(option?.value)),
                )}
              />
              {option.label}
            </div>
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default InputOptionCheckbox;
